/* eslint-disable no-script-url */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FaCog, FaFileExcel, FaTasks } from 'react-icons/fa';
import FileSaver from 'file-saver';
import Axios from 'axios';
import moment from 'moment';

import { FormattedDate, FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { Dropdown, MenuItem } from 'react-bootstrap';

import intlShape from '@/shared/utils/intlShape';
import { injectStateService, StateService } from '@/shared/services/StateService';
import api from '@/shared/api';
import config from '@/config';
import { ColumnHeader, ScrollableTable } from '@/shared/components/table';
import Checkbox from '@/shared/components/input/Checkbox';
import FormattedListValue, {
  listItemMsg,
  formatListValue,
} from '@/shared/components/FormattedListValue';
import RelationName from '@/shared/components/RelationName';
import FormattedDayMonth from '@/shared/components/FormattedDayMonth';
import CheckboxListModal from '@/shared/components/CheckboxListModal';
import guid from '@/shared/utils/guid';
import userStorage from '@/shared/services/userStorage';
import Button from '@/shared/components/Button';
import SimplePanel from '@/shared/components/SimplePanel';
import utils from '@/shared/utils/adminpulse';
import handleHttpError from '@/shared/services/HandleHttpError';
import ToastrService from '@/shared/services/ToastrService';
import ZendeskHelp from '@/shared/components/ZendeskHelp';

import { CanUpdate, showWhenPermission } from '@/shared/components/security';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import { mapInvoiceStreamName } from '@/shared/services/ObjectDisplayService';
import YesNoModal from '@/shared/components/YesNoModal';

import HoverToggle from '@/shared/components/HoverToggle';
import EditableDayMonthPicker from '@/shared/components/editable/EditableDayMonthPicker';
import EditableDatePicker from '@/shared/components/editable/EditableDatePicker';
import messages from './taskScheduleMessages';
import TaskScheduleOverviewFilters, { defaultFilters } from './TaskScheduleOverviewFilters';
import UpdateTaskScheduleModal from '../overview/modals/UpdateTaskScheduleModal';
import GlobalActionsButton from './GlobalActionsButton';
import FormattedDateShort from '../../../shared/components/FormattedDateShort';
import SignalRService from '@/shared/services/SignalRService';
import CreatePlannedTaskModal from '../overview/modals/CreatePlannedTaskModal';

const columnSelectionStorageKey = 'TaskScheduleOverviewColumns';
const paramsStorageKey = 'TaskScheduleOverviewParams';

const getTaskTemplateColumnFilters = filter =>
  Object.keys(filter)
    .filter(key => key.startsWith('tasktemplate_'))
    .reduce(
      (obj, key) => ({
        ...obj,
        [key.substr(13)]: filter[key].items.map(x => x.id),
      }),
      {},
    );

class TaskScheduleOverviewContainer extends Component {
  state = {
    allRowsSelected: false,
    showColumnSelectionModal: false,
    showUpdateTaskScheduleModal: false,
    showCreatePlannedTaskModal: false,
    creatingReport: false,
    items: [],
    taskTemplates: [],
    initialSearchParams: {
      page: 0,
      pageSize: 50,
      sortKey: 'relation',
      desc: false,
      columns: [], // Selected columns, empty array=all
      overviewFilters: {
        ...defaultFilters,
      },
    },
  };

  constructor(props) {
    super(props);

    const { initialSearchParams: orgSearchParams } = this.state;

    const { overviewFilters = {}, ...sessionParams } =
      JSON.parse(sessionStorage.getItem(paramsStorageKey)) || {};
    const selectedColumns = userStorage.getItem(columnSelectionStorageKey) || [];

    this.state.initialSearchParams = {
      ...orgSearchParams,
      ...sessionParams,
      overviewFilters: { ...defaultFilters, ...overviewFilters },
      columns: selectedColumns,
    };
  }

  componentDidMount() {
    const { intl } = this.props;
    const endpoint = `${config.appUrls.tasks}/api/TaskTemplates/Schedules/`;
    api.base.get(endpoint).then(({ data }) => {
      const { initialSearchParams: { columns } = {} } = this.state;

      const taskTemplates = data.map(x => ({
        ...x,
        label: intl.formatMessage(listItemMsg('TaskTemplate', x.id, x.name)),
        selected: columns.indexOf(x.id) >= 0,
      }));

      this.setState({
        taskTemplates,
      });
    });
    this.withoutScheduleFilterItem = {
      id: guid.empty,
      name: intl.formatMessage(messages.WithoutSchedule),
    };
  }

  componentWillUnmount() {
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to leaving page.');
    }
  }

  loadItems = searchParams => {
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to new request.');
    }
    this.cancelToken = Axios.CancelToken.source();

    const filter = searchParams.filter || {};
    const { intl } = this.props;

    const taskTemplateFilters = getTaskTemplateColumnFilters(filter);
    const { hasEndFirstFiscalYear } = filter;

    let hasEndFirstFiscalYearFilter = [];
    if (hasEndFirstFiscalYear && hasEndFirstFiscalYear.items) {
      hasEndFirstFiscalYearFilter = hasEndFirstFiscalYear.items.map(x => x);
    }
    const { hasEndFiscalYear } = filter;

    let hasEndFiscalYearFilter = [];
    if (hasEndFiscalYear && hasEndFiscalYear.items) {
      hasEndFiscalYearFilter = hasEndFiscalYear.items.map(x => x);
    }
    const endpoint = `${config.appUrls.tasks}/api/TaskSchedules/Overview/`;
    sessionStorage.setItem(
      paramsStorageKey,
      JSON.stringify({
        overviewFilters: searchParams.overviewFilters,
        sortKey: searchParams.sortKey,
        desc: searchParams.desc,
        filter,
      }),
    );

    return api.base
      .post(
        endpoint,
        {
          ...searchParams.overviewFilters,
          page: searchParams.page,
          take: searchParams.pageSize,
          sort: searchParams.sortKey,
          desc: searchParams.desc,
          columns: searchParams.columns || [],
          periodicityFilters: taskTemplateFilters,
          hasEndFirstFiscalYear: hasEndFirstFiscalYearFilter,
          hasEndFiscalYear: hasEndFiscalYearFilter,
        },
        {
          cancelToken: this.cancelToken.token,
        },
      )
      .then(({ data }) => {
        if (!data) return;

        const { allRowsSelected } = this.state;
        const newTasks = data.rows.map(e => ({
          ...e,
          selected: !!allRowsSelected,
        }));
        this.setState(state => ({
          ...state,
          items: searchParams.page > 0 ? [...state.items, ...newTasks] : [...newTasks],
          totalRows: searchParams.page === 0 ? data.totalRows : state.totalRows,
          initialSearchParams: {
            ...state.initialSearchParams,
            filter: searchParams.filter,
            sortKey: searchParams.sortKey,
            desc: searchParams.desc,
          },
        }));
        return data.rows;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) {
          return handleHttpError(intl)(error);
        }
      });
  };

  handleColumnSelection = items => {
    let selectedColumns = items.filter(x => x.selected).map(x => x.id);
    // Clear selection if all are selected
    if (selectedColumns.length === items.length) selectedColumns = [];
    userStorage.setItem(columnSelectionStorageKey, selectedColumns);

    this.setState(state => ({
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        columns: selectedColumns,
      },
      taskTemplates: [...items],
      showColumnSelectionModal: false,
    }));
  };
  createBatchActionModel = (parameters = {}) => {
    const { items, allRowsSelected, initialSearchParams = {} } = this.state;
    const currentSearchParams = JSON.parse(sessionStorage.getItem(paramsStorageKey)) || {};
    const filter = currentSearchParams.filter || {};

    const taskTemplateFilters = getTaskTemplateColumnFilters(filter);
    const { hasEndFirstFiscalYear } = filter;

    let hasEndFirstFiscalYearFilter = [];
    if (hasEndFirstFiscalYear && hasEndFirstFiscalYear.items) {
      hasEndFirstFiscalYearFilter = hasEndFirstFiscalYear.items.map(x => x);
    }

    const { hasEndFiscalYear } = filter;

    let hasEndFiscalYearFilter = [];
    if (hasEndFiscalYear && hasEndFiscalYear.items) {
      hasEndFiscalYearFilter = hasEndFiscalYear.items.map(x => x);
    }
    return {
      ...currentSearchParams.overviewFilters,
      ...parameters,
      columns: initialSearchParams.columns || [],
      sort: currentSearchParams.sortKey,
      desc: currentSearchParams.desc,
      periodicityFilters: taskTemplateFilters,
      hasEndFirstFiscalYear: hasEndFirstFiscalYearFilter,
      hasEndFiscalYear: hasEndFiscalYearFilter,
      selectAll: allRowsSelected,
      selectedRows: items ? items.filter(x => x.selected !== allRowsSelected).map(x => x.id) : [],
    };
  };

  createAuthorizationReport = () => {
    const { intl } = this.props;
    const model = this.createBatchActionModel();

    const culture = intl.locale;
    const endpoint = `${
      config.appUrls.tasks
    }/api/taskschedules/export/authorizations?culture=${culture}&connectionId=${SignalRService.getConnectionId()}`;
    this.setState({
      creatingAuthorizationReport: true,
    });

    return api.base
      .post(endpoint, model)
      .then(result => {
        ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
        this.setState(state => ({
          ...state,
          creatingAuthorizationReport: false,
        }));
      })
      .catch(error => {
        this.setState(state => ({
          ...state,
          creatingAuthorizationReport: false,
        }));
        handleHttpError(intl)(error);
      });
  };

  createReport = () => {
    const { intl } = this.props;
    const model = this.createBatchActionModel();

    const culture = intl.locale;
    const endpoint = `${
      config.appUrls.tasks
    }/api/taskschedules/export?culture=${culture}&connectionId=${SignalRService.getConnectionId()}`;
    this.setState({
      creatingReport: true,
    });

    return api.base
      .post(endpoint, model)
      .then(result => {
        ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
        this.setState(state => ({
          ...state,
          creatingReport: false,
        }));
      })
      .catch(error => {
        this.setState(state => ({
          ...state,
          creatingReport: false,
        }));
        handleHttpError(intl)(error);
      });
  };

  getCountSelected = () => {
    const { allRowsSelected, totalRows, items } = this.state;

    const count = items ? items.filter(x => x.selected !== allRowsSelected).length : 0;

    return allRowsSelected ? totalRows - count : count;
  };

  getCountSelectedFormatted() {
    const { totalRows } = this.state;
    const { intl } = this.props;
    const count = this.getCountSelected();

    return intl.formatMessage(messages.CountRelationsSelected, { count, total: totalRows });
  }

  isGlobalActionsDisabled = () => {
    const { items, allRowsSelected } = this.state;
    if (!items || !items.length) return true;
    if (items.some(x => x.selected) || allRowsSelected) return false;

    return true;
  };
  filtersChanged = filters => {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        overviewFilters: filters,
      },
    }));
  };
  resetFilters = () => {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        filter: {},
        search: {},
        overviewFilters: defaultFilters,
      },
    }));
  };

  handleSelectAllRows = () => {
    this.setState(({ items, allRowsSelected }) => ({
      allRowsSelected: !allRowsSelected,
      items: items.map(e => ({ ...e, selected: !allRowsSelected })),
    }));
  };
  handleRowSelection = row => {
    this.setState(({ items }) => ({
      items: items.map(x => (x.id === row.id ? { ...x, selected: !x.selected } : x)),
    }));
  };

  showUpdateTaskScheduleModal = () => {
    this.setState({
      showUpdateTaskScheduleModal: true,
    });
  };

  showCreatePlannedTaskModal = () => {
    this.setState(state => ({
      ...state,
      showCreatePlannedTaskModal: true,
    }));
  };

  handleUpdateTaskScheduleModalSave = ({ action, model: batchActionParameters }) => {
    if (action !== 'add' && action !== 'remove' && action !== 'createplannedtask')
      return Promise.reject();
    if (this.isGlobalActionsDisabled()) return Promise.reject(new Error('invalid'));

    const { intl, stateService } = this.props;

    let model;
    let endpoint;
    let message;

    switch (action) {
      case 'createplannedtask':
        model = this.createBatchActionModel({ task: batchActionParameters });
        endpoint = `${config.appUrls.tasks}/api/TaskSchedules/BatchCreateTask`;
        message = intl.formatMessage(messages.CreateTaskQueued);
        break;
      case 'add':
        model = this.createBatchActionModel({ taskSchedule: batchActionParameters });
        endpoint = `${config.appUrls.tasks}/api/TaskSchedules/BatchCreateSchedule`;
        message = intl.formatMessage(messages.SetTaskSchedulingQueued);
        break;
      case 'remove':
      default:
        model = this.createBatchActionModel({ taskSchedule: batchActionParameters });
        endpoint = `${config.appUrls.tasks}/api/TaskSchedules/BatchRemoveSchedule`;
        message = intl.formatMessage(messages.DeleteTaskScheduleQueued);
        break;
    }

    return api.base
      .post(endpoint, model)
      .then(({ data }) => {
        this.setState(state => ({
          ...state,
          showUpdateTaskScheduleModal: false,
          showCreatePlannedTaskModal: false,
        }));
        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;
        ToastrService.info(message, undefined, onClick);
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  createTaskForSelectedRelations = task =>
    this.handleUpdateTaskScheduleModalSave({
      action: 'createplannedtask',
      model: task,
    });

  getColumns = () => {
    const { taskTemplates, initialSearchParams: { columns = [] } = {} } = this.state;
    return columns.length ? taskTemplates.filter(x => columns.indexOf(x.id) >= 0) : taskTemplates;
  };

  updateEndFiscalYear = (item, endFiscalYear) => {
    const endFiscalYearField = {
      definition: {
        id: '3117d514-64bc-4c6e-84f7-4a39fbdc51e2',
      },
      value: endFiscalYear,
    };

    const { id: relationId } = item.relation;

    return this.updateFiscalField(relationId, endFiscalYearField, 'endFiscalYear', endFiscalYear);
  };

  updateEndFirstFiscalYear = (item, endFirstFiscalYear) => {
    const endFirstFiscalYearField = {
      definition: {
        id: 'a70d571f-f208-49a3-99ad-702e9c092303',
      },
      value: endFirstFiscalYear,
    };

    const { id: relationId } = item.relation;

    return this.updateFiscalField(
      relationId,
      endFirstFiscalYearField,
      'endFirstFiscalYear',
      endFirstFiscalYear,
    );
  };

  updateFiscalField = (relationId, model, fieldName, fieldValue) => {
    const { intl } = this.props;

    return api.base
      .put(`${config.appUrls.api}/api/relation/Put?id=${relationId}`, model)
      .then(({ data }) => {
        const { items } = this.state;
        ToastrService.changesSaved(intl);

        this.setState({
          items: items.map(e => {
            if (e.relation.id === relationId) {
              return { ...e, [fieldName]: fieldValue };
            }
            return e;
          }),
        });

        if (data.fiscalYearsUpdated) {
          const recalcDeadlinesParams = {
            relationId,
            onlyBasedOnFiscalYears: true,
            recalcSubtasks: true,
          };
          return api.base
            .post(
              `${config.appUrls.tasks}/api/plannedTasks/RecalculateDeadlinesCount`,
              recalcDeadlinesParams,
            )
            .then(({ data: recalcDeadlinesCount }) => {
              if (recalcDeadlinesCount) {
                this.setState({
                  recalcDeadlinesParams,
                  recalcDeadlinesCount,
                  showRecalcTaskDeadlinesDialog: true,
                });
              }
            });
        }
      })
      .catch(error => {
        handleHttpError(intl)(error);
        return Promise.reject();
      });
  };
  recalculateTaskDeadlines = () => {
    const { recalcDeadlinesParams } = this.state;
    const { intl } = this.props;
    return api.base
      .post(`${config.appUrls.tasks}/api/plannedTasks/RecalculateDeadlines`, recalcDeadlinesParams)
      .then(() => {
        this.setState({ showRecalcTaskDeadlinesDialog: false, recalcDeadlinesParams: undefined });
        ToastrService.changesSaved(intl);
      });
  };

  renderRow = item => {
    const columns = this.getColumns();
    const { stateService, intl } = this.props;
    const showAssignments = hasAssignments();

    return (
      <tbody className="hover" key={item.id}>
        <tr>
          <td>
            <Checkbox onClick={() => this.handleRowSelection(item)} value={item.selected} />
          </td>
          <td>
            <RelationName relation={item.relation} />
          </td>
          {showAssignments && <td className="fw-normal">{mapInvoiceStreamName(item, intl)}</td>}
          <td>
            {item.relation && (item.relation.type === 0 || item.relation.type === 1) ? (
              <EditableDatePicker
                value={item.endFirstFiscalYear}
                onChange={date => this.updateEndFirstFiscalYear(item, date)}
              />
            ) : null}
          </td>
          <td>
            {item.relation && (item.relation.type === 0 || item.relation.type === 1) ? (
              <EditableDayMonthPicker
                value={item.endFiscalYear ? moment.utc(item.endFiscalYear) : undefined}
                outputType="moment"
                onChange={date => this.updateEndFiscalYear(item, date)}
                displayValue={
                  !!item.endFiscalYear && <FormattedDayMonth value={item.endFiscalYear} />
                }
              />
            ) : null}
          </td>
          <td className="fw-normal">
            {item.nextAnnualMeeting ? <FormattedDateShort value={item.nextAnnualMeeting} /> : null}
          </td>
          {columns.map(col => {
            const schedules = item.columns[col.id];
            return (
              <td key={col.id}>
                {schedules && schedules.length > 0
                  ? schedules.map(s => (
                      <div key={s.id} className="fw-normal">
                        <Dropdown id={s.id} className="width100pct">
                          <FormattedListValue
                            list="taskperiodicity"
                            id={s.taskPeriodicityId}
                            fallback={s.taskPeriodicityName}
                          />
                          <HoverToggle bsRole="toggle" />
                          <Dropdown.Menu className="unselectable">
                            <MenuItem
                              eventKey="0"
                              onSelect={() =>
                                stateService.gotoTaskboard({
                                  relation: item.relation,
                                  taskTemplateId: col.id,
                                })
                              }
                            >
                              <i className="fa fa-th fa-fw margin5R" />
                              <FormattedMessage {...messages.OpenInTaskboard} />
                            </MenuItem>
                            <MenuItem
                              eventKey="1"
                              onSelect={() =>
                                stateService.gotoTaskOverview({
                                  taskId: col.id,
                                  periodicityId: s.taskPeriodicityId,
                                  relation: item.relation,
                                  period: 'all',
                                })
                              }
                            >
                              <FaTasks className="react-icon fa-fw margin5R" />
                              <FormattedMessage {...messages.OpenTaskOverview} />
                            </MenuItem>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    ))
                  : null}
              </td>
            );
          })}
        </tr>
      </tbody>
    );
  };

  render() {
    const {
      initialSearchParams,
      items,
      allRowsSelected,
      taskTemplates,
      showColumnSelectionModal,
      creatingReport,
      showUpdateTaskScheduleModal,
      showCreatePlannedTaskModal,
      creatingAuthorizationReport,
      showRecalcTaskDeadlinesDialog,
      recalcDeadlinesCount,
    } = this.state;
    const { intl } = this.props;
    const columns = this.getColumns();

    const hasData = items && items.length > 0;
    const hasFilters =
      JSON.stringify(initialSearchParams.overviewFilters) !== JSON.stringify(defaultFilters) ||
      (initialSearchParams.filter && Object.keys(initialSearchParams.filter).length > 0);
    const showAssignments = hasAssignments();

    return (
      <div>
        {showRecalcTaskDeadlinesDialog && (
          <YesNoModal
            show
            title={intl.formatMessage(messages.RecalculateTaskDeadlinesTitle)}
            message={intl.formatMessage(messages.RecalculateTaskDeadlinesMsg, {
              count: recalcDeadlinesCount,
            })}
            onNo={() =>
              this.setState({
                showRecalcTaskDeadlinesDialog: false,
                recalcDeadlinesParams: undefined,
              })
            }
            onSave={() => this.recalculateTaskDeadlines()}
          />
        )}
        <SimplePanel
          article="5434784349458"
          title={intl.formatMessage(messages.TaskScheduleOverview)}
        >
          <TaskScheduleOverviewFilters
            filters={initialSearchParams.overviewFilters}
            onFilterChange={filters => this.filtersChanged(filters)}
            countSelected={this.getCountSelectedFormatted()}
            onResetFilters={this.resetFilters}
            hasFilters={hasFilters}
          />
        </SimplePanel>
        <div className="row btn-row">
          <div className="col-md-12 pull-right content-right">
            {taskTemplates && taskTemplates.length > 0 && (
              <Button
                btnClassName="btn btn-xs btn-default margin10R"
                onClick={() => this.setState({ showColumnSelectionModal: true })}
              >
                <span className="margin5R">
                  <FaCog className="react-icon" />
                </span>
                <FormattedMessage {...messages.ColumnConfig} />
              </Button>
            )}
            <Button
              btnClassName="btn btn-xs btn-default margin10R"
              onClick={() => this.createReport()}
              disabled={!hasData || creatingReport}
            >
              <span className="margin5R">
                {creatingReport && <i className="fa fa-spinner fa-spin" />}
                {!creatingReport && <FaFileExcel className="file-icon" />}
              </span>
              {intl.formatMessage(messages.ExportTaskSchedules)}
            </Button>
            <Button
              btnClassName="btn btn-xs btn-default margin10R"
              onClick={this.createAuthorizationReport}
              disabled={!hasData || creatingAuthorizationReport}
            >
              <span className="margin5R">
                {creatingAuthorizationReport && <i className="fa fa-spinner fa-spin" />}
                {!creatingAuthorizationReport && <FaFileExcel className="file-icon" />}
              </span>
              {intl.formatMessage(messages.ExportAuthorizations)}
            </Button>
            <CanUpdate on="TaskSchedules">
              <GlobalActionsButton
                onUpdateTaskSchedule={this.showUpdateTaskScheduleModal}
                disabled={this.isGlobalActionsDisabled()}
                onCreateTask={this.showCreatePlannedTaskModal}
              />
            </CanUpdate>
          </div>
        </div>

        <ScrollableTable
          tableClassName="table-grid margin50B"
          loadItems={searchParams => this.loadItems(searchParams)}
          initialSearchParams={initialSearchParams}
          items={items}
          renderRow={item => this.renderRow(item)}
          noDataLabel={<FormattedMessage {...messages.NoTasksFound} />}
        >
          <ColumnHeader
            title={
              <Checkbox
                extraClass="margin2L"
                onClick={this.handleSelectAllRows}
                value={allRowsSelected}
              />
            }
          />
          <ColumnHeader title={intl.formatMessage(messages.Relation)} sortKey="relation" />

          {showAssignments ? (
            <ColumnHeader title={intl.formatMessage(messages.Assignment)} sortKey="assignment" />
          ) : null}
          <ColumnHeader
            title={intl.formatMessage(messages.EndFirstFiscalYear)}
            className="endFirstFiscalYear-column"
            filter={{
              type: 'items',
              key: `hasEndFirstFiscalYear`,
              items: [true, false],
              renderItem: item =>
                item === true
                  ? intl.formatMessage(messages.WithEndFirstFiscalYear)
                  : intl.formatMessage(messages.WithoutEndFirstFiscalYear),
            }}
          />
          <ColumnHeader
            title={intl.formatMessage(messages.EndFiscalYear)}
            sortKey="endFiscalYear"
            className="endFiscalYear-column"
            filter={{
              type: 'items',
              key: `hasEndFiscalYear`,
              items: [true, false],
              renderItem: item =>
                item === true
                  ? intl.formatMessage(messages.WithEndFiscalYear)
                  : intl.formatMessage(messages.WithoutEndFiscalYear),
            }}
          />
          <ColumnHeader
            title={intl.formatMessage(messages.NextAnnualMeeting)}
            sortKey="nextAnnualMeeting"
            className="nextAnnualMeeting-column"
          />

          {columns &&
            columns.map(col => (
              <ColumnHeader
                key={col.id}
                title={col.label}
                className="text-left"
                filter={{
                  type: 'items',
                  key: `tasktemplate_${col.id}`,
                  items: [this.withoutScheduleFilterItem, ...col.periodicities],
                  renderItem: item =>
                    formatListValue({
                      id: item.id,
                      list: 'taskperiodicity',
                      fallback: item.name,
                      intl,
                    }),
                }}
              />
            ))}
        </ScrollableTable>

        <CheckboxListModal
          title={intl.formatMessage(messages.SelectOverviewColumns)}
          items={taskTemplates}
          onSave={this.handleColumnSelection}
          onClose={() => this.setState({ showColumnSelectionModal: false })}
          show={showColumnSelectionModal}
        />

        {showUpdateTaskScheduleModal && (
          <UpdateTaskScheduleModal
            intl={intl}
            show={showUpdateTaskScheduleModal}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showUpdateTaskScheduleModal: false,
              }))
            }
            onSave={this.handleUpdateTaskScheduleModalSave}
          />
        )}

        {showCreatePlannedTaskModal && (
          <CreatePlannedTaskModal
            intl={intl}
            show 
           onClose={() =>
              this.setState(state => ({
                ...state,
                showCreatePlannedTaskModal: false,
              }))
            }
            onSave={task => this.createTaskForSelectedRelations(task)}
          />
        )}
      </div>
    );
  }
}

TaskScheduleOverviewContainer.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
};

export default showWhenPermission(
  'TaskSchedules',
  'read',
)(injectStateService(injectIntl(TaskScheduleOverviewContainer)));
